// Several files chosen or dropped at once, sent one after another: the server takes one file a request.

export interface UploadRow {
  name: string
  state: 'waiting' | 'sending' | 'done' | 'duplicate' | 'refused'
  /** Of the file's bytes sent, 0 to 1. */
  progress: number
  /** Why it was refused, or the name it already has in the library. */
  note?: string
}

interface Named {
  name: string
  type: string
}

/** Sends each accepted file in turn; a refusal is that file's own and the next one still goes. */
export async function uploadEach<F extends Named>(
  files: readonly F[],
  how: {
    accept: (file: F) => boolean
    send: (file: F, progress: (fraction: number) => void, signal?: AbortSignal) => Promise<{ duplicate?: boolean; name?: string }>
    explain: (err: unknown) => string
    notAccepted: (name: string) => string
    /** Why this file is refused before sending (its kind or size); overrides accept when set. */
    refusal?: (file: F) => string | undefined
    onChange: (rows: UploadRow[]) => void
    /** Calls the batch off: the file being sent is aborted and no other is sent. */
    signal?: AbortSignal
  },
): Promise<UploadRow[]> {
  let rows: UploadRow[] = files.map((f) => {
    const why = how.refusal ? how.refusal(f) : how.accept(f) ? undefined : how.notAccepted(f.name)
    return why === undefined ? { name: f.name, state: 'waiting', progress: 0 } : { name: f.name, state: 'refused', progress: 0, note: why }
  })
  const set = (i: number, row: UploadRow) => {
    rows = rows.map((r, j) => (j === i ? row : r))
    how.onChange(rows)
  }
  how.onChange(rows)
  for (const [i, f] of files.entries()) {
    if (how.signal?.aborted) break
    if (rows[i]!.state !== 'waiting') continue
    set(i, { name: f.name, state: 'sending', progress: 0 })
    try {
      const sent = await how.send(
        f,
        (fraction) => set(i, { name: f.name, state: 'sending', progress: Math.min(1, Math.max(0, fraction)) }),
        how.signal,
      )
      set(i, sent.duplicate ? { name: f.name, state: 'duplicate', progress: 1, note: sent.name ?? '' } : { name: f.name, state: 'done', progress: 1 })
    } catch (err) {
      set(i, { name: f.name, state: 'refused', progress: rows[i]!.progress, note: how.explain(err) })
    }
  }
  return rows
}
